/**
 * @file FormatTab.js
 * @description Format tab component for the Multi-tenant SSO Wizard. The destination URL accepts any non-empty string.
 */

import React, { useState, useEffect } from "react";
import SSOInitiationTypeDropdown from "../../shared/asyncDropdowns/SSOInitiationTypeDropdown";
import SSOVendorEnvironmentDropdown from "../../shared/asyncDropdowns/SSOVendorEnvironmentDropdown";
import SSOImportVendorDropdown from "../../shared/asyncDropdowns/SSOImportVendorDropdown";
import { CharacterLimitIndicator } from "../../helpers/CharacterLimitIndicator";

import style from "../sso.module.scss";
import { hasText } from "../../../utils/utils";
import { getText } from '../../../i18n';

const FormatTab = ({ data, onChange }) => {
  const [formData, setFormData] = useState(data);
  const [isUrlDirty, setIsUrlDirty] = useState(false);
  const urlError = isUrlDirty && !hasText(formData.url) ? "URL is required" : "";

  useEffect(() => {
    onChange(formData);
  }, [formData, onChange]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "url") {
      setIsUrlDirty(true);
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUrlLockToggle = () => {
    setFormData((prevData) => ({
      ...prevData,
      urlIsLocked: !prevData.urlIsLocked,
    }));
  };

  return (
    <div className="col-12 col-xs-12 col-md-12 col-lg-6 m-auto pb-5 p-3 p-xl-0">
      <div className="form-group mb-3 flex-lg-nowrap">
        <label htmlFor="name">
          {getText('msg_709a23220f2c')} <span className="text-danger">*</span>
        </label>
        <div className="position-relative">
          <input
            type="text"
            id="name"
            name="name"
            className="form-control"
            value={formData.name || ""}
            onChange={handleChange}
            required
            placeholder={getText('msg_6d56f3f422c6')}
            maxLength={20}
          />
          <CharacterLimitIndicator inputStr={formData.name} maxChars={20} />
        </div>
      </div>

      <div className="form-group">
          <SSOImportVendorDropdown
              selectedVendor={formData.vendorId}
              onVendorChange={vendorId =>
                  handleDropdownChange('vendorId', vendorId)
              }
              inlineLabel={false}
              isRequired={true}
              label={getText('msg_d96159ff30af')}
              placeholder={getText('msg_1b5e74225da1')}
          />
      </div>

      <div className="form-group">
        <SSOInitiationTypeDropdown
          selectedType={formData.ssoInitiationTypeId}
          onTypeChange={(typeId) =>
            handleDropdownChange("ssoInitiationTypeId", typeId)
          }
          inlineLabel={false}
          isRequired={true}
          label={getText('msg_2d0ad15e4439')}
          placeholder={getText('msg_9fbaeb45eb3c')}
        />
      </div>

      <div className="form-group">
        <SSOVendorEnvironmentDropdown
          selectedEnvironment={formData.ssoVendorEnvironmentId}
          onEnvironmentChange={(envId) =>
            handleDropdownChange("ssoVendorEnvironmentId", envId)
          }
          inlineLabel={false}
          isRequired={true}
          label={getText('msg_d1f3ee272fcd')}
          placeholder={getText('msg_476a73db5ff6')}
        />
      </div>

      <div className="form-group mb-3 flex-lg-nowrap">
        <label htmlFor="issuer">
          {getText('msg_2587e48b8114')} <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="issuer"
          name="issuer"
          className="form-control"
          value={formData.issuer || ""}
          onChange={handleChange}
          required
          placeholder={getText('msg_a483763d5e09')}
        />
      </div>

      <div className="form-group mb-3 flex-lg-nowrap">
        <label htmlFor="recipient">
          {getText('msg_903432601712')} <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="recipient"
          name="recipient"
          className="form-control"
          value={formData.recipient || ""}
          onChange={handleChange}
          required
          placeholder={getText('msg_6464f79ed7da')}
        />
      </div>

      <div className="form-group mb-3 flex-lg-nowrap">
        <label htmlFor="audience">{getText('msg_51d9934555e2')}</label>
        <input
          type="text"
          id="audience"
          name="audience"
          className="form-control"
          value={formData.audience || ""}
          onChange={handleChange}
          placeholder={getText('msg_59813ffa86e9')}
        />
      </div>

      <div className="form-group mb-3 flex-lg-nowrap position-relative">
        {formData.urlIsLocked && (
          <div className={`position-absolute ${style.formatTabLock}`}>
            <i className="bi bi-lock-fill text-muted me-2"></i>
          </div>
        )}
        <label htmlFor="url">
          {getText('msg_935336fdf425')} <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="url"
          name="url"
          className={`form-control ${urlError ? "is-invalid" : ""}`}
          value={formData.url || ""}
          onChange={handleChange}
          required
          placeholder={getText('msg_80d4bdee95ce')}
          disabled={formData.urlIsLocked}
        />
        <div className={`d-flex align-items-end ${style.formatTabActionLock}`}>
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={handleUrlLockToggle}
          >
            <i
              className={`bi bi-${
                formData.urlIsLocked ? "unlock-fill" : "lock-fill"
              }`}
            ></i>
          </button>
        </div>
        {urlError && (
          <div className="invalid-feedback d-block">{urlError}</div>
        )}
      </div>

      <div className="form-group mb-3 flex-lg-nowrap">
        <label htmlFor="partnerURL">{getText('msg_bbe3d0fad214')}</label>
        <input
          type="url"
          id="partnerURL"
          name="partnerURL"
          className="form-control"
          value={formData.partnerURL || ""}
          onChange={handleChange}
          placeholder={getText('msg_1250a434e3a2')}
          pattern="https?:\/\/.*"
        />
      </div>

      <div className="form-group">
        <div className="form-check">
          <input
            type="checkbox"
            id="signAssertion"
            name="signAssertion"
            className="form-check-input"
            checked={formData.signAssertion || false}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="signAssertion">
            {getText('msg_29992e2ba96a')}
          </label>
        </div>
      </div>

      <div className="form-group">
        <div className="form-check">
          <input
            type="checkbox"
            id="signResponse"
            name="signResponse"
            className="form-check-input"
            checked={formData.signResponse || false}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="signResponse">
            {getText('msg_4e49a46e96a1')}
          </label>
        </div>
      </div>

      <div className="form-group">
        <div className="form-check">
          <input
            type="checkbox"
            id="useRelayState"
            name="useRelayState"
            className="form-check-input"
            checked={formData.useRelayState || false}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="useRelayState">
            {getText('msg_d60e5c118d7e')}
          </label>
        </div>
      </div>

      <div className="form-group">
        <div className="form-check">
          <input
            type="checkbox"
            id="useAuthnStatement"
            name="useAuthnStatement"
            className="form-check-input"
            checked={formData.useAuthnStatement || false}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="useAuthnStatement">
            {getText('msg_4cf946e2783e')}
          </label>
        </div>
      </div>

      <div className="form-group">
        <div className="form-check">
          <input
            type="checkbox"
            id="reauthenticate"
            name="reauthenticate"
            className="form-check-input"
            checked={formData.reauthenticate || false}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="reauthenticate">
            {getText('msg_7d7334021977')}
          </label>
        </div>
      </div>
    </div>
  );
};

export default FormatTab;
